import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import type { ColumnDef } from '@tanstack/react-table';
import { Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { DataTable } from '@/components/data-table/DataTable';
import { ConfirmDialog } from '@/components/feedback/ConfirmDialog';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { formatDateTime } from '@/lib/utils/date';
import { BASE_PERMISSION } from '@/lib/constants/modules';
import { usePermission } from '@/lib/permissions/use-permission';
import { Navigate } from 'react-router-dom';
import { useBlacklist, useDeleteBlacklist } from '../api';
import type { BlacklistRow } from '../types';

export function BlacklistListPage() {
  const list = useBlacklist();
  const rows = list.data ?? [];
  const del = useDeleteBlacklist();
  const [pendingDelete, setPendingDelete] = useState<BlacklistRow | null>(null);
  const canRemove = usePermission('Number Action.View Blacklist', BASE_PERMISSION.DELETE);
  const canView = usePermission('Number Action.View Blacklist', BASE_PERMISSION.VIEW);

  const columns = useMemo<ColumnDef<BlacklistRow>[]>(
    () => {
      const base: ColumnDef<BlacklistRow>[] = [
      {
        accessorKey: 'blacklistId',
        header: 'ID',
        size: 80,
        cell: ({ row }) => (
          <span className="font-mono text-xs text-muted-foreground">
            {row.original.blacklistId}
          </span>
        ),
      },
      {
        accessorKey: 'mobile',
        header: 'Mobile',
        cell: ({ row }) => (
          <span className="font-mono tabular-nums">{row.original.mobile}</span>
        ),
      },
      {
        accessorKey: 'reqDate',
        header: 'Added on',
        size: 200,
        cell: ({ row }) =>
          row.original.reqDate ? formatDateTime(row.original.reqDate) : '—',
      },
      {
        id: 'actions',
        header: '',
        size: 60,
        enableSorting: false,
        enableHiding: false,
        cell: ({ row }) => (
          <Button
            variant="ghost"
            size="icon"
            className="h-8 w-8 text-destructive hover:text-destructive"
            onClick={() => setPendingDelete(row.original)}
            aria-label="Remove from blacklist"
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        ),
      },
      ];
      return canRemove ? base : base.filter((c) => c.id !== 'actions');
    },
    [canRemove],
  );

  if (!canView) return <Navigate to="/" replace />;

  return (
    <FillPage>
      <PageHeader
        title="Blacklist"
        description="Numbers blocked from receiving any outbound calls from this account."
        actions={
          canRemove ? (
            <Button asChild>
              <Link to="/add-voice-blacklist">
                <Plus className="h-4 w-4" />
                Add to blacklist
              </Link>
            </Button>
          ) : undefined
        }
      />

      <DataTable<BlacklistRow, unknown>
        tableId="number-actions:blacklist:v1"
        columns={columns}
        data={rows}
        isLoading={list.isLoading}
        searchPlaceholder="Search by mobile…"
        emptyTitle="No blacklisted numbers"
        emptyDescription='Click "Add to blacklist" to add your first entry, or bulk-upload a file.'
        getRowId={(row) => String(row.blacklistId)}
        initialColumnPinning={{ right: ['actions'] }}
        exportFilename="blacklist.csv"
        pageSize={25}
      />

      <ConfirmDialog
        open={pendingDelete !== null}
        onOpenChange={(o) => !o && setPendingDelete(null)}
        title="Remove from blacklist?"
        description={
          <>
            Mobile{' '}
            <span className="font-mono font-medium text-foreground">
              {pendingDelete?.mobile}
            </span>{' '}
            will be able to receive calls again.
          </>
        }
        confirmLabel="Remove"
        tone="destructive"
        loading={del.isPending}
        onConfirm={() => {
          if (!pendingDelete) return;
          del.mutate(pendingDelete.blacklistId, {
            onSuccess: (res) => {
              toast.success(res?.message ?? 'Removed from blacklist');
              setPendingDelete(null);
            },
            onError: () => toast.error('Could not remove from blacklist.'),
          });
        }}
      />
    </FillPage>
  );
}
